/**
 * 涂抹笔绘制覆盖层
 *
 * 设计要点：
 *
 * ① 线宽一致
 *    预览 SVG 使用画布像素坐标，strokeWidth 直接以画布像素为单位（随 CSS zoom 自然缩放）。
 *    提交后 FreehandElement 使用 viewBox="0 0 100 100" + vectorEffect="non-scaling-stroke"，
 *    描边仍为 strokeWidth CSS px → 两者经 CSS zoom 后等比缩放，视觉完全一致。
 *
 * ② 整次涂抹为单一元素
 *    每次"按下→拖拽→释放"产生一个"子路径"（subPath），存入 allSubPaths 队列；
 *    退出工具时将所有子路径合并为单个 SVG path d（0-100 归一化坐标），
 *    创建一个 FreehandElement。
 *
 * ③ 双击退出
 *    dblclick 事件提交所有已绘制子路径并退出工具。
 *    双击的第二个 mouseup 通过时间检测（< 300ms）跳过，避免多余路径。
 *
 * ④ Esc 取消
 *    Esc 清空所有子路径并退出（不生成元素）。
 *
 * ⑤ 退出时自动提交
 *    useEffect 清理函数在工具关闭（如点击按钮）时提交剩余路径。
 *
 * 坐标约定：
 *    预览路径使用绝对画布像素坐标。
 *    提交时归一化到 0-100 viewBox 坐标，使路径随元素缩放。
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import {
  toAbsoluteDrawCoords,
  type DrawOverlayInsertProps,
  type FrozenDrawInsertTarget,
} from '@/components/canvas/workspace/overlays/drawOverlayTypes';

interface Point { x: number; y: number; }

const MIN_DIST = 3;        // 抽稀阈值（画布像素）
const DBLCLICK_MS = 300;   // 双击时间窗口

// ─── path builders ─────────────────────────────────────────────────────────

/**
 * 二次贝塞尔穿中点平滑路径（画布像素坐标，供预览 SVG 使用）。
 * pts 为绝对画布像素，ox/oy 为偏移量（减去后得到局部坐标）。
 */
function buildSmoothPath(pts: Point[], ox = 0, oy = 0): string {
  if (pts.length === 0) return '';

  const x = (p: Point) => (p.x - ox).toFixed(1);
  const y = (p: Point) => (p.y - oy).toFixed(1);

  if (pts.length === 1) return `M ${x(pts[0])} ${y(pts[0])}`;

  let d = `M ${x(pts[0])} ${y(pts[0])}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = ((pts[i].x + pts[i + 1].x) / 2 - ox).toFixed(1);
    const my = ((pts[i].y + pts[i + 1].y) / 2 - oy).toFixed(1);
    d += ` Q ${x(pts[i])} ${y(pts[i])},${mx} ${my}`;
  }
  d += ` L ${x(pts[pts.length - 1])} ${y(pts[pts.length - 1])}`;
  return d;
}

/**
 * 二次贝塞尔穿中点平滑路径（0-100 viewBox 归一化坐标，供提交到 store 使用）。
 * ox/oy 为包围盒左上角偏移，bw/bh 为包围盒宽高（画布像素）。
 */
function buildNormalizedPath(pts: Point[], ox: number, oy: number, bw: number, bh: number): string {
  if (pts.length === 0) return '';

  const nx = (px: number) => (((px - ox) / bw) * 100).toFixed(2);
  const ny = (py: number) => (((py - oy) / bh) * 100).toFixed(2);
  const np = (p: Point) => `${nx(p.x)} ${ny(p.y)}`;

  if (pts.length === 1) return `M ${np(pts[0])}`;

  let d = `M ${np(pts[0])}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = nx((pts[i].x + pts[i + 1].x) / 2);
    const my = ny((pts[i].y + pts[i + 1].y) / 2);
    d += ` Q ${np(pts[i])},${mx} ${my}`;
  }
  d += ` L ${np(pts[pts.length - 1])}`;
  return d;
}

// ─── component ────────────────────────────────────────────────────────────

export interface FreehandToolOverlayProps extends DrawOverlayInsertProps {
  pageWidth: number;
  pageHeight: number;
  zoom: number;
}

export function FreehandToolOverlay({
  pageWidth,
  pageHeight,
  zoom,
  insertVia,
  pageId,
  originX = 0,
  originY = 0,
  activatePageOnDraw = false,
  onSessionEnd,
}: FreehandToolOverlayProps) {
  const {
    addFreehandElementAt,
    setFreehandToolActive,
    setCurrentPageId,
    freehandStrokeColor,
    freehandStrokeWidth,
  } = useCanvas();

  const svgRef = useRef<SVGSVGElement>(null);

  // 已完成的子路径列表
  const [allSubPaths, setAllSubPaths] = useState<Point[][]>([]);
  // 当前正在绘制的路径
  const [currentPath, setCurrentPath] = useState<Point[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);

  // 命令式 refs（始终最新，避免 stale closure）
  const allSubPathsRef = useRef<Point[][]>([]);
  const currentPathRef = useRef<Point[]>([]);
  const isDrawingRef = useRef(false);
  const lastUpTimeRef = useRef(0);

  // 颜色/宽度 refs，用于 cleanup 中读取最新值
  const strokeColorRef = useRef(freehandStrokeColor);
  strokeColorRef.current = freehandStrokeColor;
  const strokeWidthRef = useRef(freehandStrokeWidth);
  strokeWidthRef.current = freehandStrokeWidth;

  const insertTargetRef = useRef<FrozenDrawInsertTarget | null>(null);

  const freezeInsertTarget = useCallback(() => {
    if (!insertTargetRef.current) {
      insertTargetRef.current = {
        via: insertVia,
        pageId,
        originX,
        originY,
      };
    }
    return insertTargetRef.current;
  }, [insertVia, pageId, originX, originY]);

  // ── coordinate conversion ─────────────────────────────────────────────

  const getPos = useCallback((e: React.MouseEvent | MouseEvent): Point => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
  }, [zoom]);

  // ── commit all subpaths as a single FreehandElement ──────────────────

  const commitAll = useCallback((paths: Point[][], color: string, sw: number) => {
    if (paths.length === 0) return;

    const allPts = paths.flat();
    if (allPts.length === 0) return;

    const pad = sw / 2;
    const xs = allPts.map((p) => p.x);
    const ys = allPts.map((p) => p.y);
    const ox = Math.min(...xs) - pad;
    const oy = Math.min(...ys) - pad;
    const bw = Math.max(Math.max(...xs) - Math.min(...xs) + sw, 4);
    const bh = Math.max(Math.max(...ys) - Math.min(...ys) + sw, 4);

    // 归一化到 0-100 viewBox 坐标，使路径随元素缩放自然拉伸
    const pathData = paths
      .map((pts) => buildNormalizedPath(pts, ox, oy, bw, bh))
      .join(' ');

    const target = freezeInsertTarget();
    const abs = toAbsoluteDrawCoords(ox, oy, target.originX, target.originY);
    addFreehandElementAt({
      x: abs.x,
      y: abs.y,
      width: bw,
      height: bh,
      pathData,
      strokeColor: color,
      strokeWidth: sw,
      via: target.via,
      pageId: target.pageId,
    });
    insertTargetRef.current = null;
    onSessionEnd?.();
  }, [addFreehandElementAt, freezeInsertTarget, onSessionEnd]);

  // ── 退出时自动提交（按钮关闭工具时） ────────────────────────────────

  useEffect(() => {
    return () => {
      const paths = allSubPathsRef.current;
      if (paths.length > 0) {
        commitAll(paths, strokeColorRef.current, strokeWidthRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在 unmount 时运行

  // ── keyboard ──────────────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // 取消：清空所有路径后退出（不提交）
        allSubPathsRef.current = [];
        setAllSubPaths([]);
        insertTargetRef.current = null;
        setFreehandToolActive(false);
        onSessionEnd?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setFreehandToolActive, onSessionEnd]);

  // ── mouse handlers ────────────────────────────────────────────────────

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    freezeInsertTarget();
    if (activatePageOnDraw && pageId) {
      setCurrentPageId(pageId);
    }

    const pos = getPos(e);
    currentPathRef.current = [pos];
    setCurrentPath([pos]);
    setIsDrawing(true);
    isDrawingRef.current = true;
  }, [getPos, freezeInsertTarget, activatePageOnDraw, pageId, setCurrentPageId]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();

    const pos = getPos(e);
    const prev = currentPathRef.current;
    if (prev.length > 0) {
      const last = prev[prev.length - 1];
      const dist = Math.sqrt((pos.x - last.x) ** 2 + (pos.y - last.y) ** 2);
      if (dist < MIN_DIST) return;
    }

    const next = [...prev, pos];
    currentPathRef.current = next;
    setCurrentPath(next);
  }, [getPos]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    setIsDrawing(false);
    isDrawingRef.current = false;

    // 加入释放时的精确位置
    const pos = getPos(e);
    const prev = currentPathRef.current;
    const last = prev[prev.length - 1];
    const pts =
      last && Math.sqrt((pos.x - last.x) ** 2 + (pos.y - last.y) ** 2) >= MIN_DIST
        ? [...prev, pos]
        : prev;

    currentPathRef.current = [];
    setCurrentPath([]);

    // 双击第二击检测：跳过，由 dblclick 事件负责提交
    const now = Date.now();
    if (now - lastUpTimeRef.current < DBLCLICK_MS && allSubPathsRef.current.length > 0) {
      return;
    }
    lastUpTimeRef.current = now;

    // 有意义的路径（≥ 2 点）才加入队列
    if (pts.length >= 2) {
      const next = [...allSubPathsRef.current, pts];
      allSubPathsRef.current = next;
      setAllSubPaths(next);
    }
  }, [getPos]);

  /**
   * dblclick：提交所有已绘制路径并退出工具。
   * 双击第二次的 mouseup 已被时间检测跳过，不会有多余路径。
   */
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const paths = allSubPathsRef.current;
    allSubPathsRef.current = []; // 清空，防止 cleanup 重复提交
    setAllSubPaths([]);
    commitAll(paths, strokeColorRef.current, strokeWidthRef.current);
    setFreehandToolActive(false);
  }, [commitAll, setFreehandToolActive]);

  // ── preview rendering ─────────────────────────────────────────────────

  // 当前正在绘制路径的预览 d 字符串
  const liveD = isDrawing && currentPath.length >= 2
    ? buildSmoothPath(currentPath)
    : '';

  // 单点点按预览
  const liveDot = isDrawing && currentPath.length === 1 ? currentPath[0] : null;

  // strokeWidth 直接用画布像素（与提交后的元素单位相同），无需 /zoom
  const sw = freehandStrokeWidth;

  return (
    <svg
     
      ref={svgRef}
      viewBox={`0 0 ${pageWidth} ${pageHeight}`}
      width={pageWidth}
      height={pageHeight}
      style={{
        position: 'absolute',
        inset: 0,
        cursor: 'crosshair',
        userSelect: 'none',
        zIndex: 1000,
        overflow: 'visible',
        pointerEvents: 'auto',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onDoubleClick={handleDoubleClick}
    >
      {/* 已完成的子路径（本次 session 内，尚未提交到 store） */}
      {allSubPaths.map((pts, i) => (
        <path
          key={i}
          d={buildSmoothPath(pts)}
          fill="none"
          stroke={freehandStrokeColor}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.9}
        />
      ))}

      {/* 当前正在绘制的路径 */}
      {liveD && (
        <path
          d={liveD}
          fill="none"
          stroke={freehandStrokeColor}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.9}
        />
      )}

      {/* 单点点按圆点 */}
      {liveDot && (
        <circle
          cx={liveDot.x}
          cy={liveDot.y}
          r={sw / 2}
          fill={freehandStrokeColor}
          opacity={0.9}
        />
      )}
    </svg>
  );
}
